import { hasOpenDecision } from '../task-schedule';
import { bucketOfTask, type BucketableTask } from '../agent-task-buckets';

export type WorldActivity = 'working' | 'searching' | 'reading' | 'writing' | 'creating' | 'sending' | 'checking';
export type WorldState = WorldActivity | 'review' | 'needsYou' | 'waiting' | 'queued' | 'scheduled' | 'unknown' | 'failed' | 'done';

export interface WorldTask extends BucketableTask {
  id: string;
  title?: string;
  agentId?: string;
  reviewerAgentId?: string;
  reviewStage?: string;
  agentConversationId?: string;
  reviewerConversationId?: string;
  currentAction?: string;
  goalId?: string;
  artifacts?: string[];
  createdDate?: string;
}

export interface WorldBeat {
  conversationId: string;
  at: number;
  toolName?: string;
  actionLine?: string;
}

export interface WorldAgent { id: string; name: string }
export interface WorldPlace extends WorldAgent { x: number; z: number; radius: number }
export interface WorldActor {
  id: string;
  agentId: string;
  reviewerId?: string;
  reviewerActivity?: WorldActivity;
  goalId?: string;
  reviewStage?: string;
  artifact?: 'document' | 'design' | 'message' | 'package';
  title: string;
  state: WorldState;
  action: string;
  x: number;
  z: number;
  homeX: number;
  homeZ: number;
  seed: number;
}

export function worldActivity(tool: string = '', action: string = ''): WorldActivity {
  const text = `${tool.replace(/([a-z])([A-Z])/g, '$1 $2')} ${action}`;
  if (/search|brows|fetch|research/i.test(text)) return 'searching';
  if (/image|design|render|paint|generat.*(video|photo)/i.test(text)) return 'creating';
  if (/read|list|inspect|open.*file/i.test(text)) return 'reading';
  if (/review|test|check|validat/i.test(text)) return 'checking';
  if (/writ|draft|edit|cod|sav|creat|terminal|execut/i.test(text)) return 'writing';
  if (/send|outreach|notify/i.test(text)) return 'sending';
  return 'working';
}

export function worldTaskState(task: WorldTask, beat?: WorldBeat, now = Date.now()): WorldState {
  const bucket = bucketOfTask(task);
  if (bucket === 'done') return 'done';
  if (bucket === 'failed') return 'failed';
  if (bucket === 'waiting') return 'needsYou';
  if (task.decisionPromptsUnavailable) return 'unknown';
  if (task.status === 'IN_REVIEW' || task.reviewStage === 'submitted') return 'review';
  if (task.runInFlight === true) return worldActivity(beat?.toolName, beat?.actionLine || task.currentAction);
  if (task.status === 'PENDING') return task.notBefore && Date.parse(String(task.notBefore)) > now ? 'scheduled' : 'queued';
  if (task.status === 'WAITING' || task.runInFlight === false) return 'waiting';
  return 'unknown';
}

export function worldPlaces(agents: WorldAgent[]): WorldPlace[] {
  return agents.map((agent, index) => {
    const ring = Math.floor(index / 10);
    const count = Math.min(10, agents.length - ring * 10);
    const angle = (index % 10) * Math.PI * 2 / count - Math.PI / 2;
    const distance = 16.5 + ring * 12;
    return { ...agent, x: Math.cos(angle) * distance, z: Math.sin(angle) * distance, radius: 4.8 };
  });
}

export function worldActors(tasks: WorldTask[], places: WorldPlace[], beats: Record<string, WorldBeat>, now = Date.now(), includeDone = false): WorldActor[] {
  const byId = new Map(places.map(place => [place.id, place]));
  const slots = new Map<string, number>();
  return tasks.flatMap(task => {
    const conversation = task.status === 'IN_REVIEW' || task.reviewStage === 'submitted' ? task.reviewerConversationId : task.agentConversationId;
    const latest = conversation ? beats[conversation] : undefined;
    const beat = latest && now - latest.at < 45_000 ? latest : undefined;
    const state = worldTaskState(task, beat, now);
    if (state === 'done' && !includeDone) return [];
    const home = byId.get(task.agentId ?? '');
    const reviewer = task.reviewerAgentId ? byId.get(task.reviewerAgentId) : undefined;
    // An unknown reviewer stays at home: missing attribution must never invent a handoff to Maestro.
    const destination = state === 'review' && reviewer ? reviewer : home;
    const slotKey = destination?.id ?? '';
    const slot = slots.get(slotKey) ?? 0;
    slots.set(slotKey, slot + 1);
    const seed = [...task.id].reduce((hash, ch) => (hash * 31 + ch.charCodeAt(0)) >>> 0, 0);
    const angle = slot * 2.4;
    const radius = 1.1 + Math.min(1.2, Math.sqrt(slot) * 0.35);
    return [{ id: task.id, agentId: task.agentId ?? '', reviewerId: reviewer?.id, title: task.title ?? '', state,
      goalId: task.goalId, reviewStage: task.reviewStage, artifact: worldArtifact(task),
      reviewerActivity: state === 'review' && beat ? worldActivity(beat.toolName, beat.actionLine) : undefined,
      action: beat?.actionLine || task.currentAction || '',
      x: (destination?.x ?? 0) + Math.cos(angle) * radius,
      z: (destination?.z ?? 0) + Math.sin(angle) * radius,
      homeX: home?.x ?? 0, homeZ: home?.z ?? 0, seed }];
  });
}

export function worldArtifact(task: WorldTask): NonNullable<WorldActor['artifact']> {
  const paths = (task.artifacts || []).join(' ');
  if (/\.(png|jpe?g|svg|webp|gif)(\s|$)/i.test(paths)) return 'design';
  if (/\.(zip|tar|gz)(\s|$)/i.test(paths)) return 'package';
  const activity = worldActivity('', task.currentAction);
  return activity === 'creating' ? 'design' : activity === 'sending' ? 'message' : 'document';
}

export function actorIsWorking(state: WorldState): boolean {
  return ['working', 'searching', 'reading', 'writing', 'creating', 'sending', 'checking'].includes(state);
}

/**
 * The tasks whose loop is RUNNING for the map's purposes — the set the start animation is diffed against.
 *
 * A row held on an OPEN QUESTION is excluded, however live its run says it is. {@link worldTaskState}
 * already draws such a row as `needsYou` (its `bucketOfTask` promotion wins before `runInFlight` is read),
 * so without the same veto here the agent ran to its place and played the start animation for work parked
 * on the reader's question — while the very task it ran to was drawn as needing them. The event and the
 * state told two stories about one task.
 *
 * The veto belongs in the SET, not in the diff: a blocked task never enters it, so the instant the
 * question is answered the row reads as newly-in-flight and the animation plays THEN — on the beat the
 * loop actually resumes, which is what the animation is for.
 *
 * Pure and exported so the rule is testable without mounting the hook that uses it; the hook keeps only
 * the ref it diffs against.
 */
export function worldRunningTaskIds(tasks: WorldTask[]): Set<string> {
  return new Set(tasks.filter((task) => task.runInFlight === true && !hasOpenDecision(task)).map((task) => task.id));
}
